import { useCurrency } from "@/lib/currency";
// Live pricing table — fetches pricing categories + prices directly from the
// Bokun REST API (`/activity.json/:id` + `/activity.json/:id/price-list`).
// Renders above the booking widget. Categories without a Bokun match are hidden.
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Tag } from "lucide-react";

import { getProduct, getPriceList } from "@/lib/bokun.functions";
import { formatPrice } from "@/lib/slug";

const TICKET_LABELS: Record<string, string> = {
  ADULT: "Adulto",
  CHILD: "Niño",
  INFANT: "Bebé",
  YOUTH: "Joven",
  SENIOR: "Adulto mayor",
  STUDENT: "Estudiante",
  MILITARY: "Militar",
  FAMILY: "Familia",
  GROUP: "Grupo",
  OTHER: "Otro",
};

function localizeTitle(title: string | null | undefined, ticket: string | null): string {
  if (ticket && TICKET_LABELS[ticket]) return TICKET_LABELS[ticket];
  const t = (title ?? "").trim();
  if (!t) return "Pasajero";
  const upper = t.toUpperCase();
  if (TICKET_LABELS[upper]) return TICKET_LABELS[upper];
  const plurals: Record<string, string> = {
    ADULTS: "Adultos",
    CHILDREN: "Niños",
    KIDS: "Niños",
    INFANTS: "Bebés",
    YOUTHS: "Jóvenes",
    SENIORS: "Adultos mayores",
  };
  return plurals[upper] ?? t;
}

function ageRange(min: number | null, max: number | null): string | null {
  if (min == null && max == null) return null;
  const a = min ?? 0;
  const b = max ?? 99;
  return `${a}-${b}`;
}

export function BokunPricingTable({ productId }: { productId: string }) {
  useCurrency(); // subscribe: formatPrice reads the currency store
  const product = useQuery({
    queryKey: ["bokun", "product", productId],
    queryFn: () => getProduct({ data: { id: productId } }),
    staleTime: 5 * 60_000,
  });
  // Match the booking calendar's freshness so the per-category prices shown
  // here stay in lock-step with whatever the Bokun calendar/widget displays.
  const priceList = useQuery({
    queryKey: ["bokun", "price-list", productId],
    queryFn: () => getPriceList({ data: { id: productId } }),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });

  const rows = useMemo(() => {
    const cats = product.data?.product.pricingCategories ?? [];
    const entries = priceList.data?.entries ?? [];
    const priceById = new Map(entries.map((e) => [String(e.pricingCategoryId), e]));
    const defaultCurrency = product.data?.product.currency ?? null;

    type Row = {
      id: string;
      title: string;
      age: string | null;
      price: number | null;
      currency: string | null;
      ticket: string | null;
    };
    const out: Row[] = [];

    for (const c of cats) {
      const pr = priceById.get(c.id);
      if (!pr) continue; // hide categories without a Bokun price entry
      out.push({
        id: c.id,
        title: localizeTitle(c.title || pr.pricingCategoryTitle, pr.ticketCategory),
        age: ageRange(c.minAge, c.maxAge),
        price: pr.price,
        currency: pr.currency ?? defaultCurrency,
        ticket: pr.ticketCategory,
      });
    }

    // Fallback: no product categories returned → use price-list entries directly.
    if (out.length === 0) {
      for (const pr of entries) {
        out.push({
          id: pr.pricingCategoryId,
          title: localizeTitle(pr.pricingCategoryTitle, pr.ticketCategory),
          age: null,
          price: pr.price,
          currency: pr.currency ?? defaultCurrency,
          ticket: pr.ticketCategory,
        });
      }
    }

    const order: Record<string, number> = {
      ADULT: 0, YOUTH: 1, SENIOR: 1, STUDENT: 1, MILITARY: 1, CHILD: 2, INFANT: 3,
    };
    out.sort((a, b) => (order[a.ticket ?? ""] ?? 9) - (order[b.ticket ?? ""] ?? 9));
    return out;
  }, [product.data, priceList.data]);

  const isLoading = product.isLoading || priceList.isLoading;

  return (
    <section
      aria-labelledby="bokun-pricing-heading"
      className="rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-elevated)] sm:p-6"
    >
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Tag className="h-5 w-5 text-primary" aria-hidden />
          <h3 id="bokun-pricing-heading" className="font-display text-lg font-semibold">
            Precios
          </h3>
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" aria-hidden />
          En vivo
        </span>
      </div>

      {isLoading ? (
        <ul className="space-y-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <li
              key={i}
              className="h-10 animate-pulse rounded-md bg-muted/60"
            />
          ))}
        </ul>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Precios no disponibles por el momento.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((r) => {
            const isFree = r.price != null && r.price <= 0;
            return (
              <li
                key={r.id}
                className="flex items-center justify-between gap-3 py-2.5 text-sm sm:text-base"
              >
                <div className="min-w-0">
                  <span className="font-medium text-foreground">{r.title}</span>
                  {r.age && (
                    <span className="ml-1 text-muted-foreground">({r.age})</span>
                  )}
                </div>
                <div className="shrink-0 font-semibold text-foreground">
                  {isFree
                    ? "Gratis"
                    : r.price == null
                      ? "—"
                      : formatPrice(r.price, r.currency)}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
